import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, FileText, Search } from 'lucide-react';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import { API, PRICELISTS_PER_PAGE, formatCurrency, toggleSelectItem, toggleSelectAll, errDetail } from './adminShared';
import { DeleteAllButton } from './DeleteAllButton';
import { uploadPricelistInBatches } from './csvUtils';

const EMPTY_FORM = { hospital_name: '', item_id: '', item_name: '', item_type: 'Service', cost: '', uom: '' };

export default function PricelistsTab({ hospitals, currencies, authCfg }) {
  const [pricelists, setPricelists] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [hospitalFilter, setHospitalFilter] = useState('');
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);

  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkHospital, setBulkHospital] = useState('');
  const [bulkPreview, setBulkPreview] = useState([]);
  const [bulkProgress, setBulkProgress] = useState(null);

  const load = useCallback(async (p = page, s = search, hf = hospitalFilter) => {
    try {
      const response = await axios.get(`${API}/admin/pricelists/all`, {
        ...authCfg,
        params: { page: p, limit: PRICELISTS_PER_PAGE, search: s, hospital_filter: hf },
      });
      const data = response.data.data || [];
      setPricelists(Array.isArray(data) ? data : []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      setPricelists([]);
      setTotalPages(1);
      setTotal(0);
    }
  }, [authCfg, page, search, hospitalFilter]);

  useEffect(() => { load(page, search, hospitalFilter); }, [page, search, hospitalFilter, load]);

  const openAdd = () => { setEditing(null); setForm({ ...EMPTY_FORM, hospital_name: hospitals.length > 0 ? hospitals[0].hospital_name : '' }); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      ...item,
      hospital_name: item.hospital_name || (hospitals.length > 0 ? hospitals[0].hospital_name : ''),
      item_type: item.item_type || 'Service',
      uom: item.uom || '',
    });
    setDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/pricelists/${editing.hospital_name}/${editing.item_id}`, {
          item_name: form.item_name,
          item_type: form.item_type,
          cost: parseFloat(form.cost),
          uom: form.uom || '',
        }, authCfg);
        toast.success('Price list item updated successfully');
      } else {
        await axios.post(`${API}/admin/pricelists`, { ...form, cost: parseFloat(form.cost) }, authCfg);
        toast.success('Price list item created successfully');
      }
      setDialog(false);
      setForm(EMPTY_FORM);
      setEditing(null);
      load();
    } catch (error) {
      toast.error(errDetail(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (hospitalName, itemId) => {
    setConfirm({
      title: 'Delete price list item',
      description: `Delete price list item "${itemId}"?`,
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/admin/pricelists/${hospitalName}/${itemId}`, authCfg);
          toast.success('Price list item deleted successfully');
          load();
        } catch (error) {
          toast.error('Failed to delete price list item');
        }
      },
    });
  };

  const handleBulkDelete = () => {
    setConfirm({
      title: `Delete ${selected.length} selected price list item(s)`,
      description: 'This action cannot be undone.',
      confirmLabel: `Delete ${selected.length}`,
      destructive: true,
      onConfirm: async () => {
        setLoading(true);
        let ok = 0, fail = 0;
        for (const itemId of selected) {
          try {
            const item = pricelists.find((p) => p.item_id === itemId);
            if (item) {
              await axios.delete(`${API}/admin/pricelists/${item.hospital_name}/${itemId}`, authCfg);
              ok++;
            } else {
              fail++;
            }
          } catch {
            fail++;
          }
        }
        toast.success(`Deleted ${ok} item(s)${fail > 0 ? `, ${fail} failed` : ''}`);
        setSelected([]);
        load();
        setLoading(false);
      },
    });
  };

  const handleBulkFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target.result;
        let items = [];
        if (file.name.endsWith('.json')) {
          items = JSON.parse(content);
        } else if (file.name.endsWith('.csv')) {
          const lines = content.split('\n').filter((line) => line.trim());
          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map((v) => v.trim());
            if (values.length >= 4) {
              // New format: id,name,type,uom,cost — old format (no UOM): id,name,type,cost
              const isOldFormat = values.length === 4 || (values[3] !== '' && !isNaN(parseFloat(values[3])) && isNaN(parseFloat(values[4] || '')));
              items.push({
                item_id: values[0],
                item_name: values[1],
                item_type: values[2] || 'Service',
                cost: parseFloat(isOldFormat ? values[3] : values[4]),
                uom: isOldFormat ? '' : (values[3] || ''),
              });
            }
          }
        }
        const validItems = items.filter((item) => item.item_id && item.item_name && item.cost && !isNaN(item.cost));
        if (validItems.length === 0) {
          toast.error('No valid items found in file');
          return;
        }
        setBulkPreview(validItems);
        toast.success(`Loaded ${validItems.length} items`);
      } catch (error) {
        toast.error('Failed to parse file: ' + error.message);
      }
    };
    reader.readAsText(file);
  };

  const handleBulkUploadSubmit = async () => {
    if (!bulkHospital || bulkPreview.length === 0) {
      toast.error('Please select a hospital and upload a file');
      return;
    }
    setLoading(true);
    try {
      const { created, skipped } = await uploadPricelistInBatches(API, bulkHospital, bulkPreview, authCfg, (b, total) => setBulkProgress({ b, total }));
      toast.success(`Created ${created} items, skipped ${skipped} duplicates`);
      setBulkDialog(false);
      setBulkHospital('');
      setBulkPreview([]);
      load();
    } catch (error) {
      toast.error(errDetail(error, 'Failed to upload price list'));
    } finally {
      setLoading(false);
      setBulkProgress(null);
    }
  };

  const hospitalWithCurrency = (h) => {
    const currency = currencies.find((c) => c.code === (h.currency_code || 'USD'));
    return `${h.hospital_name} (${currency?.symbol || '$'} ${currency?.code || 'USD'})`;
  };

  return (
    <Card>
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} {...(confirm || {})} />
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Price Lists</CardTitle>
        <div className="flex gap-2">
          <DeleteAllButton
            label={hospitalFilter ? `Delete ${hospitalFilter} Price List` : 'Delete All Price Lists'}
            title={hospitalFilter ? `Delete the entire price list for ${hospitalFilter}` : 'Delete price lists for ALL hospitals'}
            description={(hospitalFilter
              ? `This permanently removes every price list item for ${hospitalFilter}.`
              : 'This permanently removes every price list item for EVERY hospital.\nTip: pick a hospital in the filter below to wipe just one hospital.')
              + '\nThis cannot be undone.'}
            url={`${API}/admin/pricelists/all${hospitalFilter ? `?hospital_name=${encodeURIComponent(hospitalFilter)}` : ''}`}
            onDone={() => { setPage(1); load(1, search, hospitalFilter); }}
            testId="delete-all-pricelists"
          />
          {selected.length > 0 && (
            <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete {selected.length} Selected
            </Button>
          )}
          <Dialog open={bulkDialog} onOpenChange={(open) => { setBulkDialog(open); if (open && !bulkHospital && hospitals.length > 0) setBulkHospital(hospitals[0].hospital_name); }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="pricelist-bulk-upload-btn">
                <FileText className="w-4 h-4 mr-2" />Bulk Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Bulk Upload Price List</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Hospital</Label>
                  <Select value={bulkHospital || (hospitals.length > 0 ? hospitals[0].hospital_name : 'none')} onValueChange={setBulkHospital}>
                    <SelectTrigger><SelectValue placeholder="Select hospital" /></SelectTrigger>
                    <SelectContent>
                      {hospitals.length === 0 ? (
                        <SelectItem value="none" disabled>No hospitals available</SelectItem>
                      ) : (
                        hospitals.map((h) => (
                          <SelectItem key={h.hospital_name} value={h.hospital_name}>{hospitalWithCurrency(h)}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Upload CSV or JSON File</Label>
                  <Input type="file" accept=".csv,.json" onChange={handleBulkFileChange} className="mt-2" />
                  <p className="text-xs text-gray-500 mt-2">
                    CSV format: item_id,item_name,item_type,uom (optional),cost<br />
                    JSON format: [{"{"}"item_id":"ID","item_name":"Name","item_type":"Service","uom":"Tablet","cost":100{"}"}]
                  </p>
                </div>
                {bulkPreview.length > 0 && (
                  <div className="border rounded-lg p-4 max-h-60 overflow-y-auto">
                    <h4 className="font-semibold mb-2">Preview ({bulkPreview.length} items)</h4>
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-2">Item ID</th>
                          <th className="text-left p-2">Item Name</th>
                          <th className="text-left p-2">Type</th>
                          <th className="text-left p-2">UoM</th>
                          <th className="text-right p-2">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkPreview.slice(0, 5).map((item, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="p-2">{item.item_id}</td>
                            <td className="p-2">{item.item_name}</td>
                            <td className="p-2">{item.item_type}</td>
                            <td className="p-2">{item.uom || '-'}</td>
                            <td className="p-2 text-right">{item.cost}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {bulkPreview.length > 5 && (
                      <p className="text-xs text-gray-500 mt-2">... and {bulkPreview.length - 5} more items</p>
                    )}
                  </div>
                )}
                <Button onClick={handleBulkUploadSubmit} disabled={loading || !bulkHospital || bulkPreview.length === 0} className="w-full">
                  {loading ? (bulkProgress ? `Uploading batch ${bulkProgress.b} of ${bulkProgress.total}…` : 'Uploading...') : `Upload ${bulkPreview.length} Items`}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={dialog} onOpenChange={setDialog}>
            <DialogTrigger asChild>
              <Button onClick={openAdd} data-testid="add-pricelist-btn">
                <Plus className="w-4 h-4 mr-2" />Add Price Item
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editing ? 'Edit Price Item' : 'Add Price Item'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Hospital</Label>
                  <Select value={form.hospital_name || (hospitals.length > 0 ? hospitals[0].hospital_name : 'none')} onValueChange={(value) => setForm({ ...form, hospital_name: value })} disabled={!!editing}>
                    <SelectTrigger><SelectValue placeholder="Select hospital" /></SelectTrigger>
                    <SelectContent>
                      {hospitals.length === 0 ? (
                        <SelectItem value="none" disabled>No hospitals available</SelectItem>
                      ) : (
                        hospitals.map((h) => (
                          <SelectItem key={h.hospital_name} value={h.hospital_name}>{hospitalWithCurrency(h)}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {form.hospital_name && (
                    <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded mt-2">
                      Cost will be in <strong>{(() => {
                        const hosp = hospitals.find((h) => h.hospital_name === form.hospital_name);
                        const curr = currencies.find((c) => c.code === (hosp?.currency_code || 'USD'));
                        return `${curr?.name || 'US Dollar'} (${curr?.symbol || '$'})`;
                      })()}</strong>
                    </p>
                  )}
                </div>
                <div>
                  <Label>Item ID</Label>
                  <Input value={form.item_id} onChange={(e) => setForm({ ...form, item_id: e.target.value })} required disabled={!!editing} />
                </div>
                <div>
                  <Label>Item Name</Label>
                  <Input value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} required />
                </div>
                <div>
                  <Label>Item Type</Label>
                  <Select value={form.item_type || 'Service'} onValueChange={(value) => setForm({ ...form, item_type: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Service">Service</SelectItem>
                      <SelectItem value="Drug">Drug</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>UoM <span className="text-gray-400 text-xs">(optional)</span></Label>
                  <Input value={form.uom || ''} onChange={(e) => setForm({ ...form, uom: e.target.value })} placeholder="e.g. Tablet, Vial, Test, Visit" data-testid="pricelist-uom-input" />
                </div>
                <div>
                  <Label>Cost {form.hospital_name && (() => {
                    const hosp = hospitals.find((h) => h.hospital_name === form.hospital_name);
                    const curr = currencies.find((c) => c.code === (hosp?.currency_code || 'USD'));
                    return `(${curr?.symbol || '$'})`;
                  })()}</Label>
                  <Input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} required />
                </div>
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? 'Saving...' : editing ? 'Update' : 'Create'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input placeholder="Search by Item ID or item name..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-10" data-testid="pricelist-tab-search-input" />
            </div>
            <Select value={hospitalFilter || 'all'} onValueChange={(val) => { setHospitalFilter(val === 'all' ? '' : val); setPage(1); }}>
              <SelectTrigger><SelectValue placeholder="All Hospitals" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Hospitals</SelectItem>
                {hospitals.map((h) => (
                  <SelectItem key={h.hospital_name} value={h.hospital_name}>{h.hospital_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {pricelists.length > 0 ? ((page - 1) * PRICELISTS_PER_PAGE) + 1 : 0} - {Math.min(page * PRICELISTS_PER_PAGE, total)} of {(total || 0).toLocaleString()} items
              {search && ` (filtered)`}
              {hospitalFilter && ` in ${hospitalFilter}`}
            </p>
            {selected.length > 0 && (
              <p className="text-sm font-semibold text-blue-600">{selected.length} selected</p>
            )}
          </div>
        </div>
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="text-center p-3 text-sm font-semibold w-12">
                <Checkbox
                  checked={selected.length === pricelists.length && pricelists.length > 0}
                  onCheckedChange={() => toggleSelectAll(pricelists, selected, setSelected, 'item_id')}
                />
              </th>
              <th className="text-left p-3 text-sm font-semibold">Hospital</th>
              <th className="text-left p-3 text-sm font-semibold">Item ID</th>
              <th className="text-left p-3 text-sm font-semibold">Item Name</th>
              <th className="text-left p-3 text-sm font-semibold">Type</th>
              <th className="text-left p-3 text-sm font-semibold">UoM</th>
              <th className="text-center p-3 text-sm font-semibold">Currency</th>
              <th className="text-right p-3 text-sm font-semibold">Cost</th>
              <th className="text-center p-3 text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pricelists.length > 0 ? (
              pricelists.map((item, index) => {
                const hospital = hospitals.find((h) => h.hospital_name === item.hospital_name);
                const currency = currencies.find((c) => c.code === (hospital?.currency_code || 'USD'));
                return (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="p-3 text-center">
                      <Checkbox
                        checked={selected.includes(item.item_id)}
                        onCheckedChange={() => toggleSelectItem(item.item_id, selected, setSelected)}
                      />
                    </td>
                    <td className="p-3 text-sm">{item.hospital_name}</td>
                    <td className="p-3 text-sm font-medium">{item.item_id}</td>
                    <td className="p-3 text-sm">{item.item_name}</td>
                    <td className="p-3 text-sm">{item.item_type}</td>
                    <td className="p-3 text-sm">{item.uom || '-'}</td>
                    <td className="p-3 text-sm text-center">
                      <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
                        {currency?.code || 'USD'}
                      </span>
                    </td>
                    <td className="p-3 text-sm text-right font-medium">{formatCurrency(item.cost, currency)}</td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(item)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(item.hospital_name, item.item_id)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="9" className="p-8 text-center text-gray-500">
                  {loading ? 'Loading price list items...' : 'No price list items found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
